import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import type { CreateTrackingDto, TrackingSyncResult, TrackingCandidate } from '@mrrmlab/api-client';
import type { Tracking } from '@mrrmlab/shared-types';
import { useApiClient } from './api-client-context';
import { trackingKeys } from './query-keys';

export function useTrackings(
  options?: Omit<UseQueryOptions<Tracking[]>, 'queryKey' | 'queryFn'>,
) {
  const client = useApiClient();
  return useQuery<Tracking[]>({
    queryKey: trackingKeys.list(),
    queryFn: ({ signal }) => client.trackings.list(signal),
    ...options,
  });
}

export function useTracking(
  id: string,
  options?: Omit<UseQueryOptions<Tracking>, 'queryKey' | 'queryFn'>,
) {
  const client = useApiClient();
  return useQuery<Tracking>({
    queryKey: trackingKeys.detail(id),
    queryFn: ({ signal }) => client.trackings.get(id, signal),
    ...options,
  });
}

export function useCreateTracking() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<Tracking, Error, CreateTrackingDto>({
    mutationFn: (dto) => client.trackings.create(dto),
    onSuccess: () => qc.invalidateQueries({ queryKey: trackingKeys.list() }),
  });
}

export function useTriggerTrackingSync() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<TrackingSyncResult, Error, void>({
    mutationFn: () => client.trackings.sync(),
    onSuccess: () => qc.invalidateQueries({ queryKey: trackingKeys.list() }),
  });
}

export function useTrackingCandidates(
  trackingId: string,
  options?: Omit<UseQueryOptions<TrackingCandidate[]>, 'queryKey' | 'queryFn'>,
) {
  const client = useApiClient();
  return useQuery<TrackingCandidate[]>({
    queryKey: trackingKeys.candidates(trackingId),
    queryFn: ({ signal }) => client.candidates.listByTracking(trackingId, signal),
    ...options,
  });
}

export function useMarkCandidateDoneFromTracking(trackingId: string) {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (candidateId: string) => client.candidates.markDone(candidateId),
    onSuccess: () => qc.invalidateQueries({ queryKey: trackingKeys.candidates(trackingId) }),
  });
}
